type BikeLike = Record<string, unknown> | null | undefined

function bikeRecord(row: BikeLike): Record<string, unknown> | null {
  if (!row || typeof row !== 'object') return null
  const nested = row.bike && typeof row.bike === 'object' ? (row.bike as Record<string, unknown>) : row
  return nested
}

function readTrimmedString(row: BikeLike, ...keys: string[]): string {
  const record = bikeRecord(row)
  if (!record) return ''
  for (const key of keys) {
    const value = record[key]
    if (value != null && String(value).trim() !== '') return String(value).trim()
  }
  return ''
}

/** Resolve go_metro_bikes registry id (preferred assignment key). */
export function readBikeRegistryId(row: BikeLike): number | undefined {
  const record = bikeRecord(row)
  if (!record) return undefined
  for (const key of ['go_metro_bike_id', 'bike_id', 'id']) {
    const value = record[key]
    if (value != null && value !== '' && Number.isFinite(Number(value))) return Number(value)
  }
  return undefined
}

/** Resolve bike device IMEI from API row. */
export function readBikeDeviceId(row: BikeLike): string {
  return readTrimmedString(row, 'deviceId', 'device_id', 'ident', 'imei')
}

/** Resolve bike number plate from API row (number_plate, licensePlate, license_plate). */
export function readBikeNumberPlate(row: BikeLike): string {
  return readTrimmedString(row, 'number_plate', 'licensePlate', 'license_plate')
}

/** Resolve bike VIN from API row. */
export function readBikeVin(row: BikeLike): string {
  return readTrimmedString(row, 'vin')
}

/** Resolve bike SIM/cell number from API row. */
export function readBikeCell(row: BikeLike): string {
  return readTrimmedString(row, 'cell', 'phone', 'sim')
}

export function withNormalizedBikeIdentityFields<T extends Record<string, unknown>>(row: T): T {
  const numberPlate = readBikeNumberPlate(row)
  const vin = readBikeVin(row)
  const registryId = readBikeRegistryId(row)
  const deviceId = readBikeDeviceId(row)
  const cell = readBikeCell(row)
  return {
    ...row,
    ...(deviceId ? { deviceId, device_id: deviceId, ident: row.ident != null ? row.ident : deviceId } : {}),
    ...(registryId != null
      ? { id: registryId, bike_id: registryId, go_metro_bike_id: registryId }
      : {}),
    ...(numberPlate ? { number_plate: numberPlate, licensePlate: numberPlate, license_plate: numberPlate } : {}),
    ...(vin ? { vin } : {}),
    ...(cell ? { cell } : {}),
  }
}

/** True when driver row is linked to the given bike (registry id or IMEI). */
export function driverMatchesBikeAssignment(driver: Record<string, unknown>, bike: BikeLike): boolean {
  const bikeRegId = readBikeRegistryId(bike)
  const driverRegRaw = driver.go_metro_bike_id ?? driver.bike_id
  if (
    bikeRegId != null &&
    driverRegRaw != null &&
    driverRegRaw !== '' &&
    Number(driverRegRaw) === bikeRegId
  ) {
    return true
  }
  const bikeImei = readBikeDeviceId(bike)
  const driverImei = readTrimmedString(driver, 'bike_device_id', 'bikeDeviceId', 'bike_imei', 'bikeImei')
  return bikeImei !== '' && driverImei !== '' && driverImei === bikeImei
}

/** Select/list key: prefer registry id, fallback to IMEI. */
export function getBikeAssignmentSelectValue(row: BikeLike): string {
  const registryId = readBikeRegistryId(row)
  if (registryId != null) return String(registryId)
  return readBikeDeviceId(row)
}
